import { ApiResult } from 'src/Libs/api'
import { AuthFetchWithResponse } from 'src/Types/api'
import { InstallationInvitationsDto } from 'src/Libs/installation-rights-management/dto'
import { InstallationInvitationModel } from 'src/Libs/installation-rights-management/models'
import { installationInvitationsToToModelTransformer } from 'src/Libs/installation-rights-management/transformer'
import { FetchProps } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { StatusCodes } from '@e3dc-react/shell/Libs/http-status-codes'
import { InstallationInvitationRequestDto } from 'src/Libs/installation-rights-management/dto/installation-invitation-request.dto'
import { InviteType } from 'src/Libs/installation-rights-management/types'
import { InstallationInviationDetailsModel } from 'src/Libs/installation-rights-management/models/installation-invitation.model'

/**
 * Returns all open Invitations to a single Installation
 *
 * @param installationId
 * @param authFetch
 */
const findAll = async (installationId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<InstallationInvitationModel[]>> => {
  const fetchProps = {
    path: `installations/${installationId}/invitations?sort=-createdAt`,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
  } as FetchProps

  const response = await authFetch<void>(fetchProps)

  if (response.status === StatusCodes.NO_CONTENT) {
    return new ApiResult([], false)
  }
  if (response.status === StatusCodes.OK) {
    const models = installationInvitationsToToModelTransformer(response.data as unknown as InstallationInvitationsDto)

    return new ApiResult(models, false)
  }
  return new ApiResult<InstallationInvitationModel[]>(null, true)
}

/**
 * Requesting an (already existing or not) user to be invited to a single Installation.
 *
 * @param authFetch
 * @param installationId
 * @param emailAddress
 * @param inviteType - 'invite' for a normal invitation, 'handover' to transfer the ownership of the installation to the invited user
 */
const requestInvite = async (
  authFetch: AuthFetchWithResponse,
  installationId: number | undefined,
  emailAddress: string,
  inviteType: InviteType = 'invite'
): Promise<ApiResult<null>> => {
  if (!installationId) return new ApiResult(null, true, new Error('No installation ID provided'))

  const fetchProps = {
    path: `installations/${installationId}/invitations`,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    method: 'post',
    payload: { inviteType, app: process.env.REACT_APP_NAME, accountId: emailAddress } as InstallationInvitationRequestDto,
  } as FetchProps

  const response = await authFetch<void>(fetchProps)

  return new ApiResult(null, response.status !== StatusCodes.CREATED)
}

const getById = async (authFetch: AuthFetchWithResponse, token: string | undefined): Promise<ApiResult<InstallationInviationDetailsModel>> => {
  if (!token) return new ApiResult<InstallationInviationDetailsModel>(null, true)

  const fetchProps = {
    path: `installations/invitations/${token}`,
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
  } as FetchProps

  const response = await authFetch<InstallationInviationDetailsModel>(fetchProps)

  if (response.status === StatusCodes.OK) {
    return new ApiResult(response.data, false)
  }

  return new ApiResult<InstallationInviationDetailsModel>(null, true)
}

const allApis = { findAll, requestInvite, getById }

export default allApis
